import { sessionApiClient } from "@planningcenter/cc-api-client"
import {
  GivingPerson,
  GivingOrganization,
  PaymentMethod,
  PaymentSource,
  Statement,
  Text2GivePhoneNumber,
  RecurringDonationsResponse,
  RecurringDonationResource,
  RecurringDonationResponse,
  NewRecurringDonationDesignationPayload,
  RecurringDonationDesignationPayload,
  RecurringDonation,
  Designation,
  Donation,
  DonationsSummary,
  InKindDonation,
  DonationResource,
  DonationsResource,
  FundResource,
  InKindDonationResource,
  InKindDonationsResource,
  PaymentSourceResource,
  PledgesResource,
  PledgeResource,
  PledgeCampaignResource,
  Pledge,
  PledgeCampaign,
  isDesignation,
  isFund,
  isPledgeCampaign,
  isGivingPerson,
  GivingPersonResponse,
  isPaymentSource,
  DonationsResourceMeta,
  IncludedGivingPersonResource,
  StatementsResource,
  StatementResource,
  PledgesResourceMeta,
  ResourceMeta,
} from "./types"
import { Fund, Schedule } from "source/giving/donate/form/types"
import { loadStripe, Stripe } from "@stripe/stripe-js"

export async function fetchGivingPerson(): Promise<GivingPerson> {
  const baseUri = "/giving/v2/me"
  const params = {
    include: "text_to_give_phone_number",
    fields: {
      TextToGivePhoneNumber:
        "default_fund_name,registration_completed,phone_number,payment_method",
    },
  }
  const response = await sessionApiClient.get(baseUri, params)

  return parseGivingPersonResponse(response)
}

function parseGivingPersonResponse(
  response: GivingPersonResponse,
): GivingPerson {
  const textToGivePhoneNumber: Text2GivePhoneNumber | null =
    response.included.length > 0 && response.included[0]
      ? {
          id: response.included[0].id,
          ...response.included[0].attributes,
          payment_method_id:
            response.included[0].relationships.payment_method.data?.id || null,
        }
      : null

  return {
    id: response.data.id,
    ...response.data.attributes,
    text_to_give_phone_number: textToGivePhoneNumber,
    has_joint_giver: response.data.relationships?.joint_giver?.data != null,
  }
}

export async function fetchStatementYears(): Promise<number[]> {
  const response = await sessionApiClient.get("/giving/v2/me/statement_years")
  return response.data.attributes.all_statement_years
}

export async function fetchStatements({
  year,
  pageParam,
}: {
  year: number | null
  pageParam: number
}): Promise<{ statements: Statement[]; meta: ResourceMeta }> {
  const queryParams = {
    year,
    per_page: 25,
    offset: pageParam,
  }

  const response: StatementsResource = await sessionApiClient.get(
    "/giving/v2/me/statements",
    queryParams,
  )

  const statements = response.data.map((item: StatementResource) => ({
    id: item.id,
    ...item.attributes,
  }))

  const meta: ResourceMeta = {
    total_count: response.meta.total_count,
    next: response.meta.next?.offset
      ? {
          offset: response.meta.next.offset,
        }
      : null,
  }

  return { statements, meta }
}

export async function fetchGivingOrganization(): Promise<GivingOrganization> {
  const response = await sessionApiClient.get("/giving/v2")
  return {
    id: response.data.id,
    in_kind_donations_enabled: response.meta.in_kind_donations,
    nondeductible_giving_enabled: response.meta.nondeductible_giving,
    ...response.data.attributes,
  }
}

export async function fetchDonationsSummary(): Promise<{
  donationsSummary: DonationsSummary
}> {
  const response = await sessionApiClient.get("/giving/v2/me/donations_summary")
  return { donationsSummary: response.data.attributes }
}

export async function fetchFunds(): Promise<Fund[]> {
  const perPage = 50
  let requestUri = "/giving/v2/funds"
  let funds: Fund[] = []

  while (requestUri) {
    const response = await sessionApiClient.get(requestUri, {
      per_page: perPage,
    })
    const responseFunds = response.data.map(
      (item: { id: string; attributes: Omit<Fund, "id"> }) => ({
        id: item.id,
        ...item.attributes,
      }),
    )
    funds = [...funds, ...responseFunds]
    requestUri = response.links?.next
  }

  return funds
}

export async function fetchPaymentMethods(): Promise<PaymentMethod[]> {
  const response = await sessionApiClient.get("/giving/v2/me/payment_methods", {
    filter: "allowed",
  })

  return response.data.map(
    (item: { id: string; attributes: Omit<PaymentMethod, "id"> }) => ({
      id: item.id,
      ...item.attributes,
    }),
  )
}

export async function fetchRecurringDonations({
  pageParam,
}: {
  pageParam: number
}): Promise<{
  recurringDonations: RecurringDonation[]
  meta: { next: { offset: number } | null }
}> {
  const queryParams = {
    include: "include=designations,designations.fund",
    sort: "on_hold_indefinitely,cached_next_occurrence",
    offset: pageParam,
  }

  const response: RecurringDonationsResponse = await sessionApiClient.get(
    "/giving/v2/me/recurring_donations",
    queryParams,
  )

  return parseRecurringDonationsResponse(response)
}

export async function fetchDonations({
  year,
  fund,
  hideNondeductibleDonations = false,
  pageParam,
}: {
  year: number
  fund: Fund | null
  hideNondeductibleDonations?: boolean
  pageParam: number
}): Promise<{
  donations: Donation[]
  meta: DonationsResourceMeta
}> {
  const queryParams = {
    include: "payment_source,person,designations,designations.fund",
    year,
    per_page: 25,
    offset: pageParam,
    ...(fund !== null && { fund: fund.slug }),
    ...(hideNondeductibleDonations && { filter: "tax_deductible" }),
  }

  const response = await sessionApiClient.get(
    "/giving/v2/me/profile_donations",
    queryParams,
  )
  return parseDonationsResponse(response)
}

function parseDonationsResponse(response: DonationsResource) {
  const designationFunds: Fund[] = parseFunds(response)

  const designations: Designation[] = parseDesignations(
    response,
    designationFunds,
  )

  const paymentSources: PaymentSource[] = response.included
    .filter(isPaymentSource)
    .map((item: PaymentSourceResource) => ({
      id: item.id,
      type: item.attributes.humanized_payment_source_type,
      name: item.attributes.name,
    }))

  const people: GivingPerson[] = response.included
    .filter(isGivingPerson)
    .map((item: IncludedGivingPersonResource) => ({
      id: item.id,
      ...item.attributes,
    }))

  const donations: Donation[] = response.data.map((item: DonationResource) => ({
    id: item.id,
    ...item.attributes,
    payment_source: paymentSources.find(
      (paymentSource) =>
        paymentSource.id === item.relationships.payment_source.data?.id,
    )!,
    person: people.find(
      (person) => person.id === item.relationships.person.data?.id,
    )!,
    designations: item.relationships.designations.data.map((designation) => {
      const includedDesignation: Designation = designations.find(
        (d) => d.id === designation.id,
      )!
      return {
        ...includedDesignation,
      }
    }),
  }))

  const meta: DonationsResourceMeta = {
    received_total_amount_cents: response.meta.received_total_amount_cents,
    total_count: response.meta.total_count,
    active_total_count: response.meta.active_total_count,
    next: response.meta.next?.offset
      ? {
          offset: response.meta.next.offset,
        }
      : null,
  }

  return { donations, meta }
}

export async function fetchInKindDonations({
  year,
  fund,
  pageParam,
}: {
  year: number
  fund: Fund | null
  pageParam: number
}): Promise<{
  inKindDonations: InKindDonation[]
  meta: ResourceMeta
}> {
  const queryParams = {
    include: "person,fund",
    year,
    include_joint_givers: true,
    per_page: 25,
    offset: pageParam,
    ...(fund !== null && { fund: fund.slug }),
  }

  const response = await sessionApiClient.get(
    "/giving/v2/me/in_kind_donations",
    queryParams,
  )
  return parseInKindDonationsResponse(response)
}

function parseInKindDonationsResponse(response: InKindDonationsResource) {
  const funds: Fund[] = parseFunds(response)

  const donors: GivingPerson[] = response.included
    .filter(isGivingPerson)
    .map((item: IncludedGivingPersonResource) => ({
      id: item.id,
      ...item.attributes,
    }))

  const inKindDonations: InKindDonation[] = response.data.map(
    (item: InKindDonationResource) => ({
      id: item.id,
      ...item.attributes,
      fund: funds.find((f) => f.id === item.relationships.fund.data?.id)!,
      person: donors.find((d) => d.id === item.relationships.person.data?.id)!,
    }),
  )

  const meta: ResourceMeta = {
    total_count: response.meta.total_count,
    next: response.meta.next?.offset
      ? {
          offset: response.meta.next.offset,
        }
      : null,
  }

  return { inKindDonations, meta }
}

function parseFunds(
  response:
    | RecurringDonationsResponse
    | RecurringDonationResponse
    | DonationsResource
    | InKindDonationsResource,
): Fund[] {
  return response.included.filter(isFund).map((item: FundResource) => ({
    id: item.id,
    ...item.attributes,
  }))
}

function parseDesignations(
  response:
    | RecurringDonationsResponse
    | RecurringDonationResponse
    | DonationsResource,
  funds: Fund[],
): Designation[] {
  return response.included.filter(isDesignation).map((item) => ({
    id: item.id,
    amount: (item.attributes.amount_cents / 100).toString(),
    fund: funds.find((fund) => fund.id === item.relationships.fund.data.id)!,
  }))
}

function parseRecurringDonationData(
  item: RecurringDonationResource | RecurringDonationResponse["data"],
  designations: Designation[],
): RecurringDonation {
  return {
    id: item.id,
    ...item.attributes,
    payment_method_id: item.relationships.payment_method.data?.id || null,
    designations: item.relationships.designations.data.map((designation) => {
      const includedDesignation = designations.find(
        (d) => d.id === designation.id,
      )!
      return { ...includedDesignation }
    }),
  }
}

function parseRecurringDonationsResponse(response: RecurringDonationsResponse) {
  const funds = parseFunds(response)
  const designations = parseDesignations(response, funds)

  const recurringDonations = response.data.map((item) =>
    parseRecurringDonationData(item, designations),
  )

  const meta = {
    next: response.meta.next?.offset
      ? {
          offset: response.meta.next.offset,
        }
      : null,
  }

  return { recurringDonations, meta }
}

export function parseRecurringDonationResponse(
  response: RecurringDonationResponse,
): RecurringDonation {
  const funds = parseFunds(response)
  const designations = parseDesignations(response, funds)

  return parseRecurringDonationData(response.data, designations)
}

export async function fetchPledges({
  pageParam,
  current,
}: {
  pageParam: number
  current: boolean
}): Promise<{
  pledges: Pledge[]
  pledgeCampaigns: PledgeCampaign[]
  jointGiver: GivingPerson | null
  meta: PledgesResourceMeta
}> {
  const queryParams = {
    include: "joint_giver,pledge_campaign.fund",
    offset: pageParam,
    filter: current ? "current" : "concluded",
  }

  const response = await sessionApiClient.get(
    "/giving/v2/me/pledges",
    queryParams,
  )

  return parsePledgesResponse(response)
}

function parsePledgesResponse(response: PledgesResource) {
  const jointGiver: GivingPerson | null = response.included.filter(
    isGivingPerson,
  )[0]
    ? {
        id: response.included.filter(isGivingPerson)[0].id,
        ...response.included.filter(isGivingPerson)[0].attributes,
      }
    : null

  const pledgeFunds: Fund[] = response.included
    .filter(isFund)
    .map((item: FundResource) => ({
      id: item.id,
      ...item.attributes,
    }))

  const pledgeCampaigns: PledgeCampaign[] = response.included
    .filter(isPledgeCampaign)
    .map((item: PledgeCampaignResource) => ({
      id: item.id,
      ...item.attributes,
      fund: pledgeFunds.find(
        (fund) => fund.id === item.relationships.fund.data.id,
      )!,
    }))

  const pledges: Pledge[] = response.data.map((item: PledgeResource) => ({
    id: item.id,
    ...item.attributes,
    joint_giver: jointGiver || null,
    pledge_campaign: pledgeCampaigns.find(
      (campaign) => campaign.id === item.relationships.pledge_campaign.data.id,
    )!,
    fund: pledgeFunds.find(
      (fund) => fund.id === item.relationships.fund.data.id,
    )!,
  }))

  const meta: PledgesResourceMeta = {
    has_concluded_pledges: response.meta.has_concluded_pledges,
    total_count: response.meta.total_count,
    next: response.meta.next?.offset
      ? {
          offset: response.meta.next.offset,
        }
      : null,
  }

  return { pledges, pledgeCampaigns, jointGiver, meta }
}

export async function fetchStartDates(
  schedule: Schedule,
  releaseHoldAt: string | null,
): Promise<string[]> {
  const response = await sessionApiClient.post(
    "/giving/v2/recurring_donation_start_dates",
    { data: { attributes: { schedule, start_date: releaseHoldAt } } },
  )
  return response.data.attributes.occurrences
}

export function includedDesignationsToData(
  designations: Designation[],
): (
  | RecurringDonationDesignationPayload
  | NewRecurringDonationDesignationPayload
)[] {
  return designations.map((designation) => {
    const base: NewRecurringDonationDesignationPayload = {
      type: "RecurringDonationDesignation",
      attributes: { amount: designation.amount },
      relationships: {
        fund: {
          data: {
            type: "Fund",
            id: designation.fund.id,
          },
        },
      },
    }

    return designation.id
      ? ({ ...base, id: designation.id } as RecurringDonationDesignationPayload)
      : base
  })
}

export async function fetchStripeObject({
  publishable_key,
  account_identifier,
}: {
  publishable_key: string
  account_identifier: string
}): Promise<Stripe | null> {
  const response = await loadStripe(publishable_key, {
    stripeAccount: account_identifier,
  })
  return response
}

export async function fetchPaymentMethodWithPendingDonation(
  gatewayIdentifier: string,
): Promise<PaymentMethod | null> {
  const response = await sessionApiClient.get(
    `/giving/v2/me/payment_methods/?where[gatewayIdentifier]=${gatewayIdentifier}&include=pending_donation`,
  )

  const paymentMethod = response.data[0]

  const pendingDonation = response.included.map((item: DonationResource) => ({
    id: item.id,
    ...item.attributes,
  }))[0]

  return {
    id: paymentMethod.id,
    ...paymentMethod.attributes,
    pendingDonation,
  }
}
